import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, ShieldCheck } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines, Reveal, CountUp } from "./motion";
import { PILLARS } from "./content";

const HERO_IMG =
  "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=1800&q=80";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.78, 0.95]);

  return (
    <section
      ref={ref}
      className="relative min-h-[100vh] overflow-hidden bg-[var(--vw-navy)] text-white isolate"
      data-testid="hero-section"
    >
      {/* Parallax image */}
      <motion.div
        className="absolute inset-0 z-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${HERO_IMG})`,
          y,
          scale,
        }}
      />
      <motion.div
        className="absolute inset-0 z-[1]"
        style={{
          background:
            "linear-gradient(115deg, rgba(10,15,36,0.95) 0%, rgba(10,15,36,0.80) 45%, rgba(10,15,36,0.55) 100%)",
          opacity: overlayOpacity,
        }}
      />
      <div className="vw-grid-bg z-[1]" />
      <div className="vw-noise" />

      {/* Top labels */}
      <div className="relative z-10 vw-container pt-32 lg:pt-40">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="flex items-center gap-3 mb-10"
        >
          <span className="vw-rule-gold"></span>
          <span className="vw-overline vw-overline--gold">
            UK Construction Supply Chain
          </span>
          <span className="hidden md:inline text-[11px] text-white/50 font-mono tracking-[0.2em] ml-4">
            EST. 2025 — BROMLEY, UK
          </span>
        </motion.div>

        <h1
          className="font-display text-[11vw] sm:text-[9vw] lg:text-[6.5rem] xl:text-[7.5rem] font-semibold leading-[0.95] tracking-[-0.035em] text-white"
          data-testid="hero-headline"
        >
          <MaskedLines text="Building Better" delay={0.15} />
          <br />
          <MaskedLines text="Supply Chains." delay={0.4} />
          <br />
          <span className="text-[var(--vw-gold)]">
            <MaskedLines text="Creating Better" delay={0.75} />
            <br />
            <MaskedLines text="Opportunities." delay={1.0} />
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.3 }}
          className="mt-10 max-w-2xl text-base lg:text-lg text-slate-200/90 leading-relaxed"
          data-testid="hero-subline"
        >
          Verified Workforce connects major construction clients with capable specialist
          contractors — while helping ambitious SMEs become compliant, commercially ready
          and equipped to deliver larger contracts.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.5 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link to="/for-clients" className="vw-btn vw-btn--primary" data-testid="hero-find-partners-btn">
            Find Delivery Partners
            <ArrowRight size={16} />
          </Link>
          <Link
            to="/delivery-partners#apply"
            className="vw-btn vw-btn--gold"
            data-testid="hero-become-partner-btn"
          >
            Become a Verified Partner
          </Link>
          <Link
            to="/readiness"
            className="group inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white px-1"
            data-testid="hero-contract-ready-btn"
          >
            Get Contract Ready
            <ArrowUpRight
              size={16}
              className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          </Link>
        </motion.div>

        {/* 4 Pillars */}
        <div className="mt-20 lg:mt-28 grid grid-cols-2 lg:grid-cols-4 border-t border-white/10">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.6 + i * 0.12 }}
              className={`px-5 py-7 border-white/10 ${i < 3 ? "lg:border-r" : ""} ${
                i % 2 === 0 ? "border-r" : ""
              } ${i < 2 ? "border-b lg:border-b-0" : ""}`}
              data-testid={`pillar-${p.key}`}
            >
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-mono text-xs text-[var(--vw-gold)] tracking-[0.22em]">
                  0{i + 1}
                </span>
                <span className="h-px flex-1 bg-white/15" />
              </div>
              <div className="font-display text-xl lg:text-2xl font-medium text-white tracking-tight">
                {p.label}
              </div>
              <p className="mt-2 text-[13px] text-white/60 leading-relaxed">
                {p.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Bottom strip */}
      <Reveal>
        <div className="relative z-10 mt-16 border-t border-white/10 bg-black/30 backdrop-blur-sm">
          <div className="vw-container py-5 flex flex-wrap items-center gap-x-10 gap-y-3 text-[11px] font-mono tracking-[0.22em] uppercase text-slate-300">
            <span className="flex items-center gap-2 text-[var(--vw-gold)]">
              <ShieldCheck size={14} /> Not a Recruitment Agency
            </span>
            <span>Not a Labour-Only Provider</span>
            <span className="hidden md:inline">Construction Supply Chain &amp; Procurement</span>
            <span className="hidden lg:inline">
              <CountUp to={24} />+ UK Trades
            </span>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
